import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Activity,
  BadgeDollarSign,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Edit2,
  Layers3,
  RadioTower,
  Search,
  XCircle,
} from "lucide-react";
import API from "../services/api";
import CustomSelect from "../components/CustomSelect";
import IpAuditModal from "../components/IpAuditModal";
import PageModal from "../components/PageModal";
import PremiumPairValue from "../components/PremiumPairValue";
import RadioButtonGroup from "../components/RadioButtonGroup";
import { useToast } from "../components/Toast";
import { getAccountSets } from "../services/sessionCache";
import { resolveAccountSetId, setStoredAccountSetId } from "../services/accountSetSelection";
import { ensureUnlocked, playError } from "../services/sound";
import {
  subscribeToOpenOrders,
  subscribeToOrderUpdates,
  subscribeToOrderProfit,
  onMessage,
  connectWS,
  subscribeToQuotes,
  subscribeToPremium,
  subscribeToPositions,
  getProfitStreamHealth,
} from "../services/wsService";
import mt4mt5Service from "../services/mt4mt5Service";
import {
  formatTradeTerminalTime,
  getTradeBrokerTerminalTimeKey,
  getTradeFinalPhaseTimestampMs,
} from "../utils/tradeTimes";
import {
  mergeSwapValue,
  withComputedTradeFields,
  mergeTradeSnapshotMetrics,
  buildBrokerScopedLookup,
  findBrokerScopedPayload,
} from "../utils/liveTradeProfit";
import { getDirectionalPremiumValue, hasNonZeroPremiumPair } from "../utils/premiumDisplay";
import { getSignedThresholdStatus } from "../utils/signedThresholdStatus";
import { normalizeTakeProfitSubmission } from "../utils/takeProfitSubmission";
import "./admin/AdminShared.css";
import "./admin/AdminBroadcast.css";
import "./admin/AdminAccounts.css";
import "./admin/AdminUsers.css";
import "./ActiveTrades.css";

const PROFIT_FRESH_MS = 5000;
const ACTIVE_TRADES_LOAD_TIMEOUT_MS = Number(
  process.env.REACT_APP_ACTIVE_TRADES_LOAD_TIMEOUT_MS || 10000,
);
const DEBUG_ACTIVE_TRADES = process.env.REACT_APP_DEBUG_ACTIVE_TRADES === "true";

function debugActiveTrades(...args) {
  if (DEBUG_ACTIVE_TRADES) {
    console.log(...args);
  }
}

function normalizeActiveTrade(trade) {
  return {
    ...withComputedTradeFields(trade),
    broker1ProfitAt: trade.broker1ProfitAt ?? 0,
    broker2ProfitAt: trade.broker2ProfitAt ?? 0,
    scalpingMode: Boolean(trade.scalpingMode),
    scalpingConfirmationCount: trade.scalpingConfirmationCount ?? 0,
    scalpingLastConfirmedAt: trade.scalpingLastConfirmedAt ?? null,
  };
}

function getTradeActionErrorMessage(err, fallback = "Request failed") {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.msg ||
    err?.message ||
    fallback
  );
}

function getIpAuditEntries(record, { includeManualClose = false } = {}) {
  const entries = [];

  if (record?.createIp) entries.push({ label: "Created", value: String(record.createIp) });
  if (record?.updateIp) entries.push({ label: "Updated", value: String(record.updateIp) });
  if (includeManualClose && record?.manualCloseIp) {
    entries.push({ label: "Closed", value: String(record.manualCloseIp) });
  }

  return entries;
}

export default function ActiveTrades() {
  const toast = useToast();
  const [accountSets, setAccountSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState("");
  const [activeTrades, setActiveTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setRefreshing] = useState(false);
  const [closingTrades, setClosingTrades] = useState(new Set());
  const [totalPnL, setTotalPnL] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("openTime");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedPair, setSelectedPair] = useState("");
  const [selectedAuditIp, setSelectedAuditIp] = useState("");
  const [tpModalTradeId, setTpModalTradeId] = useState(null);
  const [tpValues, setTpValues] = useState({});
  const [savingTP, setSavingTP] = useState(new Set());
  const [savingScalping, setSavingScalping] = useState(new Set());
  const [tpModes, setTpModes] = useState({});
  useEffect(() => {
    ensureUnlocked();
  }, []);
  const [plHealth, setPlHealth] = useState({ healthy: false, resubRecent: false });
  useEffect(() => {
    const id = setInterval(() => setPlHealth(getProfitStreamHealth()), 1000);
    return () => clearInterval(id);
  }, []);
  const [futureQuote, setFutureQuote] = useState(null);
  const [spotQuote, setSpotQuote] = useState(null);
  const [buyPremium, setBuyPremium] = useState(0);
  const [sellPremium, setSellPremium] = useState(0);
  const [premiumLive, setPremiumLive] = useState(false);
  const [collectorStale, setCollectorStale] = useState(false);
  const [marketClosed, setMarketClosed] = useState(false);
  const [tpPausedMeta, setTpPausedMeta] = useState(null);
  const [positionsSource, setPositionsSource] = useState("");
  const collectorProfitAtRef = useRef(0);
  const positionsSourceRef = useRef("");
  const pendingRefreshRef = useRef(null);
  const activeTradesFetchInFlightRef = useRef(false);
  const activeTradesRefreshQueuedRef = useRef(false);
  useEffect(() => {
    positionsSourceRef.current = positionsSource;
  }, [positionsSource]);

  const money = (v) => `$${(Number(v) || 0).toFixed(2)}`;
  const formatFeedTimestamp = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-GB", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  };
  const formatOpenTime = (value) => {
    return formatTradeTerminalTime(value, "");
  };

  const normalizeQuoteSource = (source) => {
    const value = String(source || "").toLowerCase();
    if (!value) return "";
    if (value.includes("collector")) return "collector";
    if (value.includes("ws_quote") || value.includes("websocket") || value.startsWith("ws"))
      return "ws";
    if (value.includes("api") || value.includes("rest")) return "rest";
    if (
      value.includes("database") ||
      value.includes("ws-db") ||
      value.includes("db") ||
      value.includes("cache")
    )
      return "db";
    return value;
  };

  const buildQuoteSourceLabel = (future, spot) => {
    const futureSrc = normalizeQuoteSource(future?.source);
    const spotSrc = normalizeQuoteSource(spot?.source);
    if (!futureSrc && !spotSrc) return "";
    if (futureSrc && spotSrc && futureSrc === spotSrc) return futureSrc;
    if (futureSrc && spotSrc) return `future:${futureSrc} spot:${spotSrc}`;
    return futureSrc || spotSrc;
  };

  const toTitleCase = (value) => {
    const text = String(value || "").trim();
    if (!text) return "";
    return text.replace(/\b[a-z]/g, (char) => char.toUpperCase());
  };

  const formatSource = (s) => {
    const val = String(s || "")
      .trim()
      .toLowerCase();
    if (val === "db") return "DB";
    return toTitleCase(val);
  };
  const buildSummarySourceLabel = (quoteSource, positionSource) => {
    const q = formatSource(quoteSource);
    const p = formatSource(positionSource);
    if (!q && !p) return null;
    if (q && p && q === p) return q;
    const parts = [];
    if (q) parts.push(`Feeds ${q}`);
    if (p) parts.push(`Trades ${p}`);
    return parts.join(" · ");
  };
  const maskLadderGroupId = (value) => {
    const text = String(value || "").trim();
    if (!text) return "—";
    if (text.length <= 8) return text;
    return `${text.slice(0, 4)}***${text.slice(-4)}`;
  };
  const handleProfitUpdatesArray = (updates) => {
    if (!Array.isArray(updates) || updates.length === 0) return;
    const now = Date.now();
    const hasCollector = updates.some((u) => String(u?.source || "").toLowerCase() === "collector");
    if (hasCollector) {
      collectorProfitAtRef.current = now;
    } else if (
      collectorProfitAtRef.current &&
      now - collectorProfitAtRef.current < PROFIT_FRESH_MS
    ) {
      return;
    }
    const lookup = buildBrokerScopedLookup(updates);

    setActiveTrades((prev) =>
      prev.map((trade) => {
        let touched = false;
        const next = { ...trade };

        const b1 = findBrokerScopedPayload(trade, 1, lookup);
        if (b1) {
          if (b1.profit !== trade.broker1Profit) {
            next.broker1Profit = b1.profit ?? 0;
          }
          next.broker1Swap = mergeSwapValue(trade.broker1Swap, b1.swap);
          next.broker1ProfitAt = now;
          touched = true;
        }

        const b2 = findBrokerScopedPayload(trade, 2, lookup);
        if (b2) {
          if (b2.profit !== trade.broker2Profit) {
            next.broker2Profit = b2.profit ?? 0;
          }
          next.broker2Swap = mergeSwapValue(trade.broker2Swap, b2.swap);
          next.broker2ProfitAt = now;
          touched = true;
        }

        if (touched) {
          return withComputedTradeFields(next);
        }
        return trade;
      }),
    );
  };

  const loadAccountSets = useCallback(async () => {
    try {
      const accountSetsData = await getAccountSets();
      if (Array.isArray(accountSetsData)) {
        setAccountSets(accountSetsData);
        if (accountSetsData.length > 0) {
          const selectedId = resolveAccountSetId(accountSetsData);
          setSelectedSetId(selectedId);
          if (selectedId) setStoredAccountSetId(selectedId);
        } else {
          setSelectedSetId("");
          setActiveTrades([]);
          setTotalPnL(0);
          setLoading(false);
          setRefreshing(false);
        }
      } else {
        setAccountSets([]);
        setSelectedSetId("");
        setActiveTrades([]);
        setTotalPnL(0);
        setLoading(false);
        setRefreshing(false);
      }
    } catch (err) {
      console.error("Failed to load account sets:", err);
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadActiveTrades = useCallback(
    async (isRefresh = false) => {
      if (!selectedSetId) {
        setActiveTrades([]);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (activeTradesFetchInFlightRef.current) {
        activeTradesRefreshQueuedRef.current = true;
        return;
      }

      activeTradesFetchInFlightRef.current = true;

      try {
        if (isRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
        const symbolsOnlyMessage = (msg) =>
          /symbols\s*-?only|collector\s*-?only/i.test(String(msg || ""));
        let res;

        try {
          res = await API.get(`/trading/active-trades?accountSetId=${selectedSetId}`, {
            timeout: ACTIVE_TRADES_LOAD_TIMEOUT_MS,
          });
          if (res.data?.success === false && symbolsOnlyMessage(res.data?.message)) {
            res = await API.get(`/account-sets/${selectedSetId}/active-trades`, {
              timeout: ACTIVE_TRADES_LOAD_TIMEOUT_MS,
            });
          }
        } catch (err) {
          if (symbolsOnlyMessage(err?.response?.data?.message)) {
            res = await API.get(`/account-sets/${selectedSetId}/active-trades`, {
              timeout: ACTIVE_TRADES_LOAD_TIMEOUT_MS,
            });
          } else {
            throw err;
          }
        }

        const trades = res.data?.trades || res.data?.data?.trades || [];
        const ok = res.data?.success !== false;

        if (ok) {
          const tradesWithProfits = (Array.isArray(trades) ? trades : []).map(normalizeActiveTrade);

          setActiveTrades((prev) => {
            if (!isRefresh) {
              const initialPnL = tradesWithProfits.reduce(
                (sum, trade) => sum + (trade.totalProfit || 0),
                0,
              );
              setTotalPnL(initialPnL);
              return tradesWithProfits;
            }

            const prevById = new Map(prev.map((t) => [t.tradeId, t]));
            const merged = tradesWithProfits.map((t) => {
              const p = prevById.get(t.tradeId);
              if (!p) return t;

              const mergedTrade = {
                ...mergeTradeSnapshotMetrics(t, p),
                broker1ProfitAt: t.broker1ProfitAt ?? 0,
                broker2ProfitAt: t.broker2ProfitAt ?? 0,
              };
              if (t.broker1Profit == null && p.broker1Profit != null) {
                mergedTrade.broker1ProfitAt = p.broker1ProfitAt || mergedTrade.broker1ProfitAt;
              }
              if (t.broker2Profit == null && p.broker2Profit != null) {
                mergedTrade.broker2ProfitAt = p.broker2ProfitAt || mergedTrade.broker2ProfitAt;
              }
              return mergedTrade;
            });

            const mergedPnL = merged.reduce((sum, trade) => sum + (trade.totalProfit || 0), 0);
            setTotalPnL(mergedPnL);
            return merged;
          });
        } else {
          console.error("Failed to load active trades:", res.data.message);
          setActiveTrades([]);
        }
      } catch (err) {
        console.error("Failed to load active trades:", err);
      } finally {
        activeTradesFetchInFlightRef.current = false;
        setLoading(false);
        setRefreshing(false);
        if (activeTradesRefreshQueuedRef.current) {
          activeTradesRefreshQueuedRef.current = false;
          setTimeout(() => loadActiveTrades(true), 150);
        }
      }
    },
    [selectedSetId],
  );

  useEffect(() => {
    loadAccountSets();
  }, [loadAccountSets]);

  useEffect(() => {
    if (selectedSetId) {
      loadActiveTrades();
      mt4mt5Service.initializeAccountSet(selectedSetId);
      const accountSet = accountSets.find((set) => (set._id || set.id) === selectedSetId);
      if (accountSet) {
        if (accountSet.futureSymbol && accountSet.spotSymbol) {
          subscribeToPremium(selectedSetId, accountSet.futureSymbol, accountSet.spotSymbol);
        }
      }
      setFutureQuote(null);
      setSpotQuote(null);
      setBuyPremium(0);
      setSellPremium(0);
      setPremiumLive(false);
      setCollectorStale(false);
      setTpPausedMeta(null);
      collectorProfitAtRef.current = 0;
      setPositionsSource("");
    }
  }, [selectedSetId, accountSets, loadActiveTrades]);

  useEffect(() => {
    if (!selectedSetId) return undefined;

    const unsubscribeClosedTrades = onMessage("closed_trade_created", (closedTradeData) => {
      debugActiveTrades("Order closed - refreshing active trades:", closedTradeData);
      loadActiveTrades(true);
    });
    const unsubscribeTradeClosed = onMessage("trade_closed", (tradeClosedData) => {
      debugActiveTrades("Trade closed via WebSocket - refreshing active trades:", tradeClosedData);
      loadActiveTrades(true);
    });

    const unsubscribeOpenOrders = onMessage("open_orders_update", (msg) => {
      const payload = msg?.data ?? msg;
      const accountSetId = payload?.accountSetId;
      const orders = Array.isArray(payload?.orders) ? payload.orders : [];

      if (accountSetId !== selectedSetId) return;

      if (payload?.source) {
        const nextSource = normalizeQuoteSource(payload.source);

        setPositionsSource((prev) => {
          if (
            String(prev || "")
              .toLowerCase()
              .includes("collector") &&
            nextSource !== "collector"
          ) {
            return prev;
          }

          return nextSource;
        });
      }

      const source = String(payload?.source || "").toLowerCase();
      const fromCollector = source.includes("collector");
      const preferCollector = String(positionsSourceRef.current || "")
        .toLowerCase()
        .includes("collector");
      const allowProfitUpdate = fromCollector || !preferCollector;
      const byKey = new Map();
      const plainTicketCounts = new Map();

      for (const order of orders) {
        const ticketKey = String(order?.ticket ?? "").trim();
        if (!ticketKey) continue;
        plainTicketCounts.set(ticketKey, (plainTicketCounts.get(ticketKey) || 0) + 1);
      }

      for (const o of orders) {
        const keys = [];
        if (o.brokerId) {
          keys.push(`${o.brokerId}:${o.ticket}`);
        }
        if (o.brokerPosition != null) {
          keys.push(`${o.brokerPosition}:${o.ticket}`);
        }
        if (plainTicketCounts.get(String(o.ticket)) === 1) {
          keys.push(String(o.ticket));
        }
        keys.forEach((key) => byKey.set(key, o));
      }

      setActiveTrades((prev) => {
        const updated = prev.map((t) => {
          const o1Keys = [`${t.broker1Id}:${t.broker1Ticket}`, `1:${t.broker1Ticket}`];
          if (plainTicketCounts.get(String(t.broker1Ticket)) === 1) {
            o1Keys.push(String(t.broker1Ticket));
          }
          const o1 = o1Keys.map((key) => byKey.get(key)).find((order) => order);
          const o2Keys = [`${t.broker2Id}:${t.broker2Ticket}`, `2:${t.broker2Ticket}`];
          if (plainTicketCounts.get(String(t.broker2Ticket)) === 1) {
            o2Keys.push(String(t.broker2Ticket));
          }
          const o2 = o2Keys.map((key) => byKey.get(key)).find((order) => order);
          const next = { ...t };
          if (
            allowProfitUpdate &&
            o1 &&
            (fromCollector || next.broker1Profit === undefined || next.broker1Profit === null)
          ) {
            next.broker1Profit = Number(o1.profit) || 0;
          }
          if (o1) {
            next.broker1Swap = mergeSwapValue(next.broker1Swap, o1.swap);
          }
          if (
            allowProfitUpdate &&
            o2 &&
            (fromCollector || next.broker2Profit === undefined || next.broker2Profit === null)
          ) {
            next.broker2Profit = Number(o2.profit) || 0;
          }
          if (o2) {
            next.broker2Swap = mergeSwapValue(next.broker2Swap, o2.swap);
          }
          if (o1?.openPrice) next.broker1OpenPrice = Number(o1.openPrice);
          if (o2?.openPrice) next.broker2OpenPrice = Number(o2.openPrice);

          return withComputedTradeFields(next);
        });
        setTotalPnL(updated.reduce((s, t) => s + (t.totalProfit || 0), 0));
        return updated;
      });
    });
    const unsubscribeBalance = onMessage("balance", () => {});
    const unsubscribePositions = onMessage("positions_update", (msg) => {
      const payload = msg?.data ?? msg;
      const accountSetId = payload?.accountSetId;

      if (accountSetId !== selectedSetId) return;

      if (payload?.source) {
        const nextSource = normalizeQuoteSource(payload.source);

        setPositionsSource((prev) => {
          if (
            String(prev || "")
              .toLowerCase()
              .includes("collector") &&
            nextSource !== "collector"
          ) {
            return prev;
          }

          return nextSource;
        });
      }

      const source = String(payload?.source || "").toLowerCase();
      const fromCollector = source.includes("collector");
      const mt5Data = payload.mt5Data || [];
      const mt4Data = payload.mt4Data || [];
      const lookup = buildBrokerScopedLookup([...mt5Data, ...mt4Data]);
      setActiveTrades((prev) => {
        const updatedTrades = prev.map((trade) => {
          const next = { ...trade };

          const p1 = findBrokerScopedPayload(trade, 1, lookup);
          const p2 = findBrokerScopedPayload(trade, 2, lookup);
          if (
            p1 &&
            (fromCollector || next.broker1Profit === undefined || next.broker1Profit === null)
          ) {
            next.broker1Profit = p1.profit ?? 0;
          }
          if (p1) {
            next.broker1Swap = mergeSwapValue(next.broker1Swap, p1.swap);
          }
          if (
            p2 &&
            (fromCollector || next.broker2Profit === undefined || next.broker2Profit === null)
          ) {
            next.broker2Profit = p2.profit ?? 0;
          }
          if (p2) {
            next.broker2Swap = mergeSwapValue(next.broker2Swap, p2.swap);
          }

          return withComputedTradeFields(next);
        });
        const newTotal = updatedTrades.reduce((sum, trade) => sum + (trade.totalProfit || 0), 0);
        setTotalPnL(newTotal);

        return updatedTrades;
      });
    });
    const unsubscribeOrderUpdate = onMessage("order_update", (msg) => {
      const payload = msg?.data ?? msg;
      const accountSetId = msg?.accountSetId ?? payload?.accountSetId;
      if (accountSetId && accountSetId !== selectedSetId) return;

      const ticket = String(payload?.ticket || "");
      const brokerPosition = Number(payload?.brokerPosition);
      const isClosed = Boolean(payload?.closeTime && payload?.closeTime !== "0001-01-01T00:00:00");
      if (!ticket || (brokerPosition !== 1 && brokerPosition !== 2)) return;

      if (isClosed) {
        loadActiveTrades(true);
        return;
      }
      setActiveTrades((prev) =>
        prev.map((trade) => {
          const matchB1 = brokerPosition === 1 && String(trade.broker1Ticket) === ticket;
          const matchB2 = brokerPosition === 2 && String(trade.broker2Ticket) === ticket;
          if (!matchB1 && !matchB2) return trade;
          const next = { ...trade };
          if (payload?.tradeTimes && typeof payload.tradeTimes === "object") {
            next.tradeTimes = payload.tradeTimes;
          }
          if (matchB1) {
            if (payload?.openPrice != null) next.broker1OpenPrice = Number(payload.openPrice);
            if (payload?.openTime) next.broker1OpenTime = payload.openTime;
          } else if (matchB2) {
            if (payload?.openPrice != null) next.broker2OpenPrice = Number(payload.openPrice);
            if (payload?.openTime) next.broker2OpenTime = payload.openTime;
          }
          return next;
        }),
      );
    });

    const unsubscribePendingOrders = onMessage("pending_order_update", (msg) => {
      const payload = msg?.data ?? msg;
      const accountSetId = payload?.accountSetId;
      if (accountSetId && accountSetId !== selectedSetId) return;
      const status = String(payload?.status || "");
      if (!status) return;
      if (pendingRefreshRef.current) {
        clearTimeout(pendingRefreshRef.current);
      }
      pendingRefreshRef.current = setTimeout(
        () => {
          loadActiveTrades(true);
        },
        status === "Executing" ? 300 : 600,
      );
    });

    const unsubscribeScalpingUpdate = onMessage("active_trade_scalping_update", (msg) => {
      const data = msg?.data ?? msg;
      const incomingAccountSetId = msg?.accountSetId ?? data?.accountSetId;
      if (
        !data?.tradeId ||
        (incomingAccountSetId && String(incomingAccountSetId) !== String(selectedSetId))
      )
        return;
      setActiveTrades((prev) =>
        prev.map((t) =>
          t.tradeId === data.tradeId
            ? {
                ...t,
                scalpingMode:
                  data.scalpingMode !== undefined ? Boolean(data.scalpingMode) : t.scalpingMode,
                scalpingConfirmationCount: data.scalpingConfirmationCount ?? 0,
                scalpingLastConfirmedAt: data.scalpingLastConfirmedAt ?? t.scalpingLastConfirmedAt,
              }
            : t,
        ),
      );
    });
    const fallbackRefreshInterval = setInterval(() => {
      loadActiveTrades(true);
    }, 10000);

    connectWS(selectedSetId);
    subscribeToOrderUpdates(selectedSetId);
    subscribeToPositions(selectedSetId);
    subscribeToOpenOrders(selectedSetId);
    const unsubscribeProfitStream = subscribeToOrderProfit(selectedSetId, handleProfitUpdatesArray);

    return () => {
      unsubscribeProfitStream?.();
      unsubscribeOpenOrders();
      unsubscribeBalance();
      unsubscribePositions();
      unsubscribeClosedTrades();
      unsubscribeTradeClosed();
      unsubscribeOrderUpdate();
      unsubscribePendingOrders();
      if (unsubscribeScalpingUpdate) unsubscribeScalpingUpdate();
      if (pendingRefreshRef.current) {
        clearTimeout(pendingRefreshRef.current);
      }
      clearInterval(fallbackRefreshInterval);
    };
  }, [selectedSetId, loadActiveTrades]);
  useEffect(() => {
    const sum = activeTrades.reduce((s, t) => s + (Number(t.totalProfit) || 0), 0);
    setTotalPnL(sum);
  }, [activeTrades]);
  useEffect(() => {
    const selectedAccountSet = accountSets.find((s) => (s._id || s.id) === selectedSetId);
    const locked = !!selectedAccountSet?.symbolsLocked;
    if (locked) return;

    if (futureQuote && spotQuote) {
      const newBuyPremium = (futureQuote.ask || 0) - (spotQuote.bid || 0);
      const newSellPremium = (futureQuote.bid || 0) - (spotQuote.ask || 0);
      setBuyPremium(newBuyPremium);
      setSellPremium(newSellPremium);
      setPremiumLive(true);
      setTpPausedMeta(null);
    }
  }, [futureQuote, spotQuote, selectedSetId, accountSets]);
  useEffect(() => {
    const selectedAccountSet = accountSets.find((s) => (s._id || s.id) === selectedSetId);
    if (!selectedAccountSet || !selectedSetId) return;
    if (
      !selectedAccountSet.symbolsLocked ||
      !selectedAccountSet.futureSymbol ||
      !selectedAccountSet.spotSymbol
    )
      return;

    connectWS(selectedSetId);
    subscribeToPremium(
      selectedSetId,
      selectedAccountSet.futureSymbol,
      selectedAccountSet.spotSymbol,
    );
    const unsub = onMessage("premium_update", (msg) => {
      if (!msg || String(msg.accountSetId) !== String(selectedSetId)) return;
      const bp =
        typeof msg.buyPremium === "number" ? msg.buyPremium : Number(msg.buypremium || 0) || 0;
      const sp =
        typeof msg.sellPremium === "number" ? msg.sellPremium : Number(msg.sellpremium || 0) || 0;
      setBuyPremium(bp);
      setSellPremium(sp);
      setPremiumLive(Boolean(msg.premiumLive));
      setCollectorStale(Boolean(msg.collectorStale));
      setMarketClosed(Boolean(msg.marketClosed));
      setTpPausedMeta(null);
    });
    const unsubError = onMessage("error", (msg) => {
      const payload = msg?.data ?? msg;
      const incomingAccountSetId = msg?.accountSetId ?? payload?.accountSetId;
      if (incomingAccountSetId && String(incomingAccountSetId) !== String(selectedSetId)) return;

      const status = String(payload?.status || "").toLowerCase();
      const reason = String(payload?.reason || "").toLowerCase();
      const marketClosed = Boolean(payload?.marketClosed);
      const lastKnownPremium = Number(payload?.lastKnownPremium);
      const lastKnownProfit = Number(payload?.lastKnownProfit);
      const hasLastKnownPremium = Number.isFinite(lastKnownPremium);
      const hasLastKnownProfit = Number.isFinite(lastKnownProfit);

      if (status !== "paused" && !marketClosed && !hasLastKnownPremium && !hasLastKnownProfit)
        return;

      setPremiumLive(false);
      setCollectorStale(false);
      setBuyPremium(0);
      setSellPremium(0);
      setTpPausedMeta({
        message: String(payload?.message || ""),
        status,
        reason,
        marketClosed,
        lastKnownPremium: hasLastKnownPremium ? lastKnownPremium : null,
        lastKnownProfit: hasLastKnownProfit ? lastKnownProfit : null,
        lastKnownAt: payload?.lastKnownAt || "",
        lastKnownProfitAt: payload?.lastKnownProfitAt || "",
      });
    });
    return () => {
      try {
        unsub && unsub();
      } catch {}
      try {
        unsubError && unsubError();
      } catch {}
    };
  }, [selectedSetId, accountSets]);
  useEffect(() => {
    const selectedAccountSet = accountSets.find((s) => (s._id || s.id) === selectedSetId);

    if (!selectedAccountSet || !selectedSetId) {
      return;
    }
    if (
      selectedAccountSet.symbolsLocked &&
      selectedAccountSet.futureSymbol &&
      selectedAccountSet.spotSymbol
    ) {
      debugActiveTrades("ActiveTrades: Subscribing to quotes for premium display:", {
        futureSymbol: selectedAccountSet.futureSymbol,
        spotSymbol: selectedAccountSet.spotSymbol,
      });
      connectWS(selectedSetId);

      const handleQuoteUpdate = (data) => {
        debugActiveTrades("ActiveTrades: Quote update received:", data);

        const incomingSetId = data?.accountSetId;
        if (incomingSetId && String(incomingSetId) !== String(selectedSetId)) return;

        if (data.futureSymbol === selectedAccountSet.futureSymbol && data.futureQuote) {
          setFutureQuote(data.futureQuote);
        }
        if (data.spotSymbol === selectedAccountSet.spotSymbol && data.spotQuote) {
          setSpotQuote(data.spotQuote);
        }
      };

      subscribeToQuotes(
        selectedSetId,
        selectedAccountSet.futureSymbol,
        selectedAccountSet.spotSymbol,
        handleQuoteUpdate,
      );

      const unsubscribeQuotes = onMessage("quote_update", handleQuoteUpdate);

      return () => {
        unsubscribeQuotes();
      };
    } else {
      debugActiveTrades("ActiveTrades: Skipping quote subscription - symbols not locked");
    }
  }, [selectedSetId, accountSets]);
  const getOpeningPremiumValue = useCallback(
    (trade) =>
      (parseFloat(trade.broker1OpenPrice) || 0) - (parseFloat(trade.broker2OpenPrice) || 0),
    [],
  );

  const isPremiumDataAvailable = useCallback(() => {
    return hasNonZeroPremiumPair({ buyPremium, sellPremium }) || premiumLive;
  }, [buyPremium, premiumLive, sellPremium]);

  const getCurrentPremiumValueForTrade = useCallback(
    (trade) => {
      if (isPremiumDataAvailable()) {
        const directionalPremium = getDirectionalPremiumValue(trade.broker1Direction, "exit", {
          buyPremium,
          sellPremium,
        });
        if (directionalPremium !== null) return directionalPremium;
      }
      return parseFloat(trade.currentPremium) || 0;
    },
    [buyPremium, isPremiumDataAvailable, sellPremium],
  );

  const getPremiumStatusMessage = () => {
    if (tpPausedMeta?.status === "paused") {
      if (tpPausedMeta.lastKnownPremium !== null) {
        const ts = formatFeedTimestamp(tpPausedMeta.lastKnownAt);
        return `Last known premium ${tpPausedMeta.lastKnownPremium.toFixed(2)}${ts ? ` • ${ts}` : ""}`;
      }
      if (tpPausedMeta.marketClosed || tpPausedMeta.reason === "market_closed") {
        return "Market likely closed.";
      }
      return "Premium feed is paused.";
    }
    if (collectorStale && isPremiumDataAvailable() && marketClosed) {
      return "Showing last known premium.";
    }
    if (collectorStale && isPremiumDataAvailable()) {
      return "Showing last known premium.";
    }
    if (!isPremiumDataAvailable()) {
      return "Waiting for premium.";
    }
    return null;
  };
  const getPremiumFeedLabel = () => {
    if (tpPausedMeta?.status === "paused") return "Paused";
    if (!isPremiumDataAvailable()) return "Waiting";
    if (premiumLive) return "Live";
    if (collectorStale && marketClosed) return "Market Closed";
    return collectorStale ? "Stale" : "Waiting";
  };

  const quoteSourceLabel = buildQuoteSourceLabel(futureQuote, spotQuote);
  const summarySourceLabel = buildSummarySourceLabel(quoteSourceLabel, positionsSource);
  const closeTradeDisabledReason = marketClosed
    ? "Market is closed. Close is available only during live market."
    : collectorStale
      ? "Market data is stale. Close is available only on live market."
      : !premiumLive
        ? "Waiting for live market data before close."
        : "";
  const canCloseTrades = closeTradeDisabledReason === "";

  const saveTPValue = async (tradeId, newTPValue, tpMode) => {
    try {
      setSavingTP((prev) => new Set(prev).add(tradeId));
      const { takeProfit: takeProfitValue, takeProfitMode } = normalizeTakeProfitSubmission(
        newTPValue,
        tpMode,
        roundTpNumber,
      );
      const res = await API.put(`/trading/update-tp`, {
        tradeId,
        takeProfit: takeProfitValue,
        takeProfitMode: takeProfitMode,
      });

      if (res.data.success) {
        setActiveTrades((prev) =>
          prev.map((trade) =>
            trade.tradeId === tradeId
              ? {
                  ...trade,
                  takeProfit: takeProfitValue,
                  takeProfitMode: takeProfitMode,
                }
              : trade,
          ),
        );
        setTpModalTradeId(null);
        setTpValues((prev) => ({ ...prev, [tradeId]: "" }));
        setTpModes((prev) => ({ ...prev, [tradeId]: "" }));
      } else {
        toast.error(`Failed to update TP: ${res.data.message || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Failed to save TP:", err);
      toast.error(`Failed to save TP: ${err.message || "Unknown error"}`);
    } finally {
      setSavingTP((prev) => {
        const newSet = new Set(prev);
        newSet.delete(tradeId);
        return newSet;
      });
    }
  };

  const startEditingTP = (tradeId, currentTP, currentTPMode) => {
    setTpModalTradeId(tradeId);
    setTpValues((prev) => ({ ...prev, [tradeId]: formatTpDraftValue(currentTP) }));
    setTpModes((prev) => ({ ...prev, [tradeId]: currentTPMode || "None" }));
  };

  const cancelEditingTP = (tradeId) => {
    setTpModalTradeId((current) => (current === tradeId ? null : current));
    setTpValues((prev) => ({ ...prev, [tradeId]: "" }));
    setTpModes((prev) => ({ ...prev, [tradeId]: "" }));
  };

  const closeTrade = async (trade) => {
    if (!canCloseTrades) {
      toast.error(closeTradeDisabledReason || "Close is available only on live market.");
      try {
        playError();
      } catch {}
      return;
    }

    try {
      setClosingTrades((prev) => new Set(prev).add(trade.tradeId));
      const res = await API.post("/trading/close-trade", {
        tradeId: trade.tradeId,
        reason: "Manual (UI Close)",
      });
      if (!res.data?.success) {
        throw new Error(res.data?.message || "close-trade failed");
      }
      await loadActiveTrades(true);
      toast.success(res.data?.message || `Trade ${trade.tradeId} closed successfully`);
    } catch (err) {
      console.error("Close failed:", err);
      const msg = getTradeActionErrorMessage(err, "Unknown error");
      toast.error(`Close failed: ${msg}`);
      try {
        playError();
      } catch {}
    } finally {
      setClosingTrades((prev) => {
        const next = new Set(prev);
        next.delete(trade.tradeId);
        return next;
      });
    }
  };

  const updateTradeScalpingMode = async (trade, nextScalpingMode) => {
    const previousTrade = { ...trade };

    setSavingScalping((prev) => new Set(prev).add(trade.tradeId));
    setActiveTrades((prev) =>
      prev.map((item) =>
        item.tradeId === trade.tradeId ? { ...item, scalpingMode: nextScalpingMode } : item,
      ),
    );

    try {
      const res = await API.put("/trading/update-scalping", {
        tradeId: trade.tradeId,
        scalpingMode: nextScalpingMode,
      });

      if (!res.data?.success || !res.data?.trade) {
        throw new Error(res.data?.message || "Failed to update scalping mode");
      }

      const normalizedTrade = normalizeActiveTrade({
        ...previousTrade,
        ...res.data.trade,
      });

      setActiveTrades((prev) =>
        prev.map((item) => (item.tradeId === trade.tradeId ? normalizedTrade : item)),
      );
    } catch (err) {
      setActiveTrades((prev) =>
        prev.map((item) => (item.tradeId === trade.tradeId ? previousTrade : item)),
      );

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unknown error";
      toast.error(`Failed to update scalping mode: ${msg}`);
    } finally {
      setSavingScalping((prev) => {
        const next = new Set(prev);
        next.delete(trade.tradeId);
        return next;
      });
    }
  };

  const selectedAccountSet = accountSets.find((s) => (s._id || s.id) === selectedSetId);
  const accountSetOptions = accountSets.map((set) => ({
    value: set._id || set.id,
    label: set.name,
  }));
  const pageSizeOptions = useMemo(
    () => [
      { value: "10", label: "10" },
      { value: "50", label: "50" },
      { value: "100", label: "100" },
    ],
    [],
  );
  const pairFilterOptions = useMemo(() => {
    const map = new Map();
    activeTrades.forEach((trade) => {
      const symbol1 = trade.broker1Symbol || "";
      const symbol2 = trade.broker2Symbol || "";
      if (!symbol1 && !symbol2) return;
      const key = `${symbol1}|${symbol2}`;
      if (!map.has(key)) {
        map.set(key, {
          value: key,
          label: symbol2 ? `${symbol1}/${symbol2}` : symbol1 || symbol2 || "Unknown",
        });
      }
    });
    return [{ value: "", label: "All Pairs" }, ...Array.from(map.values())];
  }, [activeTrades]);
  const pnlToneClass = (value) => {
    if (value > 0) return "active-trades-value-positive";
    if (value < 0) return "active-trades-value-negative";
    return "active-trades-value-neutral";
  };
  const getLatencyBadgeClass = (latency) => {
    const value = Number(latency);
    if (!Number.isFinite(value)) return "admin-badge-neutral";
    if (value <= 120) return "admin-badge-success";
    if (value <= 250) return "admin-badge-warning";
    return "admin-badge-danger";
  };
  const roundTpNumber = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return null;
    return Math.round(numericValue * 100) / 100;
  };
  const formatTpDraftValue = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const roundedValue = roundTpNumber(value);
    if (!Number.isFinite(roundedValue)) return "";
    return String(roundedValue);
  };
  const handleTpDraftChange = (tradeId, rawValue) => {
    if (rawValue === "") {
      setTpValues((prev) => ({ ...prev, [tradeId]: "" }));
      return;
    }

    if (!/^-?\d*\.?\d*$/.test(rawValue)) {
      return;
    }

    setTpValues((prev) => ({ ...prev, [tradeId]: rawValue }));
  };
  const handleTpDraftBlur = (tradeId) => {
    setTpValues((prev) => ({
      ...prev,
      [tradeId]: formatTpDraftValue(prev[tradeId]),
    }));
  };
  const formatTpValue = (value) => {
    const roundedValue = roundTpNumber(value);
    if (!Number.isFinite(roundedValue)) return value;
    return roundedValue.toFixed(2);
  };
  const getDeficitPremiumValue = useCallback(
    (trade) => {
      const openingPremium = getOpeningPremiumValue(trade);
      const currentPremium = getCurrentPremiumValueForTrade(trade);
      return openingPremium - currentPremium;
    },
    [getCurrentPremiumValueForTrade, getOpeningPremiumValue],
  );
  const getTpDisplayValue = (trade) => {
    if (trade.takeProfitMode === "None" || !trade.takeProfit) return "Not Set";
    if (trade.takeProfitMode === "Premium") return formatTpValue(trade.takeProfit);
    if (trade.takeProfitMode === "Amount") return `$${formatTpValue(trade.takeProfit)}`;
    return String(trade.takeProfit);
  };
  const getTpModeLabel = (trade) => {
    if (trade.takeProfitMode === "Premium") return "Deficit Premium";
    if (trade.takeProfitMode === "Amount") return "Amount";
    return "No Target";
  };
  const getTpRemainingParts = (trade) => {
    if (trade.takeProfitMode === "None" || !trade.takeProfit) {
      return { value: "", suffix: "No TP configured", highlight: false, visible: true };
    }

    const takeProfitValue = parseFloat(trade.takeProfit);
    if (!Number.isFinite(takeProfitValue)) {
      return { value: "", suffix: "", highlight: false, visible: false };
    }

    if (trade.takeProfitMode === "Premium") {
      if (!isPremiumDataAvailable()) {
        return { value: "", suffix: "", highlight: false, visible: false };
      }
      const thresholdStatus = getSignedThresholdStatus(
        getDeficitPremiumValue(trade),
        takeProfitValue,
      );
      if (!thresholdStatus) {
        return { value: "", suffix: "", highlight: false, visible: false };
      }
      if (thresholdStatus.reached) {
        return { value: "", suffix: "Target reached", highlight: false, visible: true };
      }
      return {
        value: `${thresholdStatus.distance.toFixed(2)}`,
        suffix: " away from target",
        highlight: false,
        visible: true,
      };
    }

    if (trade.takeProfitMode === "Amount") {
      const thresholdStatus = getSignedThresholdStatus(trade.totalProfit, takeProfitValue);
      if (!thresholdStatus) {
        return { value: "", suffix: "", highlight: false, visible: false };
      }
      if (thresholdStatus.reached) {
        return { value: "", suffix: "Target reached", highlight: true, visible: true };
      }
      return {
        value: `$${thresholdStatus.distance.toFixed(2)}`,
        suffix: " away from target",
        highlight: true,
        visible: true,
      };
    }

    return { value: "", suffix: "", highlight: false, visible: false };
  };
  const activeTpTrade = useMemo(
    () => activeTrades.find((trade) => trade.tradeId === tpModalTradeId) || null,
    [activeTrades, tpModalTradeId],
  );
  const activeTpMode = activeTpTrade
    ? tpModes[activeTpTrade.tradeId] || activeTpTrade.takeProfitMode || "None"
    : "None";
  const activeTpValue = activeTpTrade ? tpValues[activeTpTrade.tradeId] || "" : "";

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortDirection, sortKey, pageSize, selectedPair, selectedSetId]);

  useEffect(() => {
    setSelectedPair("");
  }, [selectedSetId]);

  const handleSort = (key) => {
    setSortDirection((currentDirection) => {
      if (sortKey === key) {
        return currentDirection === "asc" ? "desc" : "asc";
      }
      return "asc";
    });
    setSortKey(key);
  };

  const renderHeaderSort = (label, key) => {
    const active = sortKey === key;
    return (
      <button
        type="button"
        className={`active-trades-sort${active ? " active-trades-sort-active" : ""}`}
        onClick={() => handleSort(key)}
      >
        <span>{label}</span>
        {active ? (
          <ChevronDown
            className={`active-trades-sort-icon${sortDirection === "desc" ? " active-trades-sort-icon-desc" : ""}`}
          />
        ) : (
          <ChevronsUpDown className="active-trades-sort-icon" />
        )}
      </button>
    );
  };

  const processedTrades = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const getCurrentPremiumValue = (trade) => {
      return getCurrentPremiumValueForTrade(trade);
    };

    const filtered = activeTrades.filter((trade) => {
      if (selectedPair) {
        const [symbol1, symbol2] = selectedPair.split("|");
        const pairMatch =
          (!symbol1 || trade.broker1Symbol === symbol1) &&
          (!symbol2 || trade.broker2Symbol === symbol2);
        if (!pairMatch) return false;
      }
      if (!term) return true;
      return [
        trade.tradeId,
        trade.broker1Ticket,
        trade.broker2Ticket,
        trade.broker1Direction,
        trade.broker2Direction,
        trade.status,
        trade.takeProfitMode,
        trade.broker1Symbol,
        trade.broker2Symbol,
      ]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(term));
    });

    const toNumber = (value) => parseFloat(value) || 0;

    return [...filtered].sort((left, right) => {
      const getComparable = (trade) => {
        switch (sortKey) {
          case "ticket1":
            return String(trade.broker1Ticket || "");
          case "ticket2":
            return String(trade.broker2Ticket || "");
          case "direction":
            return String(trade.broker1Direction || "");
          case "lots":
            return toNumber(trade.broker1Volume);
          case "broker1Profit":
            return toNumber(trade.broker1Profit);
          case "broker2Profit":
            return toNumber(trade.broker2Profit);
          case "openingPremium":
            return getOpeningPremiumValue(trade);
          case "currentPremium":
            return getCurrentPremiumValue(trade);
          case "deficitPremium":
            return getDeficitPremiumValue(trade);
          case "openTime":
            return getTradeFinalPhaseTimestampMs(trade, "open") ?? 0;
          case "swap":
            return toNumber(trade.swap);
          case "totalProfit":
          default:
            return toNumber(trade.totalProfit);
        }
      };

      const a = getComparable(left);
      const b = getComparable(right);
      if (a < b) return sortDirection === "asc" ? -1 : 1;
      if (a > b) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [
    activeTrades,
    getCurrentPremiumValueForTrade,
    getDeficitPremiumValue,
    getOpeningPremiumValue,
    searchTerm,
    selectedPair,
    sortDirection,
    sortKey,
  ]);

  const totalFilteredTrades = processedTrades.length;
  const pageCount = Math.max(1, Math.ceil(totalFilteredTrades / pageSize));
  const paginatedTrades = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return processedTrades.slice(startIndex, startIndex + pageSize);
  }, [currentPage, pageSize, processedTrades]);
  const rowStart = totalFilteredTrades === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rowEnd =
    totalFilteredTrades === 0 ? 0 : Math.min(currentPage * pageSize, totalFilteredTrades);
  const skeletonRows = useMemo(() => Array.from({ length: 6 }), []);

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);

  const hasActiveTrades = activeTrades.length > 0;
  const plBadgeToneClass = hasActiveTrades
    ? plHealth.healthy
      ? "admin-badge-success"
      : plHealth.resubRecent
        ? "admin-badge-warning"
        : "admin-badge-neutral"
    : "admin-badge-neutral";
  const plBadgeLabel = hasActiveTrades
    ? plHealth.healthy
      ? "P&L Healthy"
      : plHealth.resubRecent
        ? "P&L Re-Subscribed"
        : "P&L Idle"
    : "P&L Standby";

  return (
    <div className="admin-page admin-broadcast-page active-trades-page">
      <div className="active-trades-shell">
        <div className="admin-broadcast-header">
          <div>
            <h1>
              <Activity className="admin-broadcast-header-icon" />
              <span>Active Trades</span>
            </h1>
            <p>
              Monitor live positions, premiums, TP progress, and execution state with the same
              no-shadow admin styling.
            </p>
          </div>
        </div>

        <div className="admin-broadcast-summary-grid active-trades-summary-grid">
          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <Layers3 size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>Account Set</span>
              <strong>{selectedAccountSet?.name || "No Set"}</strong>
              <small>
                {selectedAccountSet
                  ? `${selectedAccountSet.brokers?.[0]?.terminal}-${selectedAccountSet.brokers?.[1]?.terminal}`
                  : "Select a workspace"}
              </small>
            </div>
          </div>
          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <Activity size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>Active Trades</span>
              <strong>{activeTrades.length}</strong>
              <small>Platform-executed trades only</small>
            </div>
          </div>
          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <BadgeDollarSign size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>Open P&amp;L</span>
              <strong className={pnlToneClass(totalPnL)}>{money(totalPnL)}</strong>
              <small>{summarySourceLabel || "Live summary"}</small>
            </div>
          </div>
          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <RadioTower size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>Premium Feed</span>
              <strong>{getPremiumFeedLabel()}</strong>
              <small>{getPremiumStatusMessage() || "Fresh premium data available"}</small>
            </div>
          </div>
        </div>

        <section className="admin-card admin-broadcast-card">
          <div className="admin-card-heading">
            <div className="admin-broadcast-card-copy">
              <div className="admin-card-title">
                <Activity />
                <span>Active Trades Monitor</span>
              </div>
              <p>Monitor live trades, manage take-profit targets, and track premium movements.</p>
            </div>
            <span className={`admin-badge active-trades-monitor-badge ${plBadgeToneClass}`}>
              {plBadgeLabel}
            </span>
          </div>

          <div className="active-trades-toolbar-primary">
            <div className="admin-field-group admin-users-toolbar-search">
              <label className="admin-label">
                <Search size={13} />
                Search
              </label>
              <input
                type="text"
                className="admin-input"
                placeholder="Search by ticket, symbol, direction, or status"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="admin-field-group active-trades-account-field">
              <label className="admin-label">Account Set</label>
              <CustomSelect
                value={selectedSetId}
                onChange={(value) => {
                  setSelectedSetId(value);
                  setStoredAccountSetId(value);
                }}
                options={accountSetOptions}
                placeholder="Select account set"
              />
            </div>
            <div className="admin-field-group active-trades-pair-field">
              <label className="admin-label">Symbol Pair</label>
              <CustomSelect
                value={selectedPair}
                onChange={setSelectedPair}
                options={pairFilterOptions}
                placeholder="All Pairs"
                disabled={pairFilterOptions.length <= 1}
              />
            </div>
            <div className="admin-field-group active-trades-count-field">
              <label className="admin-label">Count</label>
              <CustomSelect
                value={String(pageSize)}
                onChange={(value) => setPageSize(Number(value))}
                options={pageSizeOptions}
              />
            </div>
          </div>

          {selectedAccountSet && (
            <div className="admin-meta active-trades-account-meta">
              <div className="active-trades-account-meta-details">
                <span>
                  Broker 1: {selectedAccountSet.brokers[0]?.terminal} (
                  {selectedAccountSet.brokers[0]?.accountNumber}-
                  {selectedAccountSet.brokers[0]?.brokerName})
                </span>
                <span>
                  Broker 2: {selectedAccountSet.brokers[1]?.terminal} (
                  {selectedAccountSet.brokers[1]?.accountNumber}-
                  {selectedAccountSet.brokers[1]?.brokerName})
                </span>
              </div>
              <div className="active-trades-account-pnl">
                <span className="active-trades-account-pnl-label">Total P&amp;L</span>
                <strong className={pnlToneClass(totalPnL)}>{money(totalPnL)}</strong>
              </div>
            </div>
          )}

          <div className="admin-data-table-wrap admin-users-table-wrap active-trades-table-wrap">
            <table className="admin-data-table active-trades-table">
              <thead>
                <tr>
                  <th className="active-trades-header-cell">S. No.</th>
                  <th className="active-trades-header-cell">IP Audit</th>
                  <th className="active-trades-header-cell">
                    {renderHeaderSort(
                      `Broker1 Ticket (${selectedAccountSet?.brokers?.[0]?.terminal || "MT4"})`,
                      "ticket1",
                    )}
                  </th>
                  <th className="active-trades-header-cell">
                    {renderHeaderSort(
                      `Broker2 Ticket (${selectedAccountSet?.brokers?.[1]?.terminal || "MT5"})`,
                      "ticket2",
                    )}
                  </th>
                  <th className="active-trades-header-cell">
                    {renderHeaderSort("Direction", "direction")}
                  </th>
                  <th className="active-trades-header-cell">
                    {renderHeaderSort("Lot Size", "lots")}
                  </th>
                  <th className="active-trades-header-cell">
                    {renderHeaderSort("Opened", "openTime")}
                  </th>
                  <th className="active-trades-header-cell">
                    {renderHeaderSort("Broker1 Profit", "broker1Profit")}
                  </th>
                  <th className="active-trades-header-cell">
                    {renderHeaderSort("Broker2 Profit", "broker2Profit")}
                  </th>
                  <th className="active-trades-header-cell">
                    {renderHeaderSort("Opening Premium", "openingPremium")}
                  </th>
                  <th className="active-trades-header-cell">Current Premium</th>
                  <th className="active-trades-header-cell">
                    {renderHeaderSort("Deficit Premium", "deficitPremium")}
                  </th>
                  <th className="active-trades-header-cell">Latency</th>
                  <th className="active-trades-header-cell">{renderHeaderSort("Swap", "swap")}</th>
                  <th className="active-trades-header-cell">
                    {renderHeaderSort("Current Profit", "totalProfit")}
                  </th>
                  <th className="active-trades-header-cell">Scalp</th>
                  <th className="active-trades-header-cell">Ladder Info</th>
                  <th className="active-trades-header-cell">Current TP</th>
                  <th className="active-trades-header-cell">Set TP</th>
                  <th className="active-trades-header-cell">Close Trade</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  skeletonRows.map((_, index) => (
                    <tr
                      key={`active-trades-skeleton-${index}`}
                      className="admin-table-skeleton-row"
                    >
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-btn" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-btn" />
                      </td>
                    </tr>
                  ))
                ) : activeTrades.length === 0 ? (
                  <tr className="admin-accounts-empty-table-row">
                    <td colSpan={20} className="admin-accounts-empty-table-cell">
                      <div className="admin-accounts-empty-table-state">
                        {accountSets.length === 0
                          ? "No linked account sets found."
                          : "No platform-executed trades found."}
                      </div>
                    </td>
                  </tr>
                ) : paginatedTrades.length === 0 ? (
                  <tr className="admin-accounts-empty-table-row">
                    <td colSpan={20} className="admin-accounts-empty-table-cell">
                      <div className="admin-accounts-empty-table-state">
                        No active trades match the current filters.
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedTrades.map((trade, index) => {
                    const ipAuditEntries = getIpAuditEntries(trade);
                    const broker1OpenedAt = getTradeBrokerTerminalTimeKey(trade, "broker1", "open");
                    const broker2OpenedAt = getTradeBrokerTerminalTimeKey(trade, "broker2", "open");
                    const openingPremiumValue = getOpeningPremiumValue(trade);
                    const currentPremiumValue = getCurrentPremiumValueForTrade(trade);
                    const hasPremiumData = isPremiumDataAvailable();
                    const deficitValue = openingPremiumValue - currentPremiumValue;
                    const isCloseBusy = closingTrades.has(trade.tradeId);
                    const isCloseDisabled = isCloseBusy || !canCloseTrades;
                    return (
                      <tr key={trade.tradeId}>
                        <td className="active-trades-cell">{rowStart + index}</td>
                        <td className="active-trades-cell active-trades-ip-audit-cell">
                          {ipAuditEntries.length ? (
                            <div className="admin-ip-audit">
                              {ipAuditEntries.map((entry) => (
                                <button
                                  key={`${entry.label}-${entry.value}`}
                                  type="button"
                                  className="admin-ip-audit-trigger"
                                  onClick={() => setSelectedAuditIp(entry.value)}
                                  title={`Inspect ${entry.value}`}
                                >
                                  <span className="admin-ip-audit-label">{entry.label}</span>
                                  <span className="admin-ip-audit-value">{entry.value}</span>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span className="admin-ip-audit-empty">—</span>
                          )}
                        </td>
                        <td className="active-trades-cell">
                          <div className="active-trades-ticket-wrap">
                            <span className="active-trades-ticket-id">{trade.broker1Ticket}</span>
                          </div>
                        </td>
                        <td className="active-trades-cell">
                          <div className="active-trades-ticket-wrap">
                            <span className="active-trades-ticket-id">
                              {trade.broker2Ticket || "—"}
                            </span>
                          </div>
                        </td>
                        <td className="active-trades-cell">
                          <span
                            className={`admin-badge ${String(trade.broker1Direction || "").toLowerCase() === "buy" ? "admin-badge-danger" : "admin-badge-success"}`}
                          >
                            {trade.broker1Direction || "—"}/{trade.broker2Direction || "—"}
                          </span>
                        </td>
                        <td className="active-trades-cell">{trade.broker1Volume}</td>
                        <td className="active-trades-cell">
                          <div className="active-trades-open-time">
                            <span className="active-trades-open-time-line">
                              <span className="active-trades-open-time-name">
                                {selectedAccountSet?.brokers?.[0]?.brokerName || "Broker 1"}
                              </span>
                              {broker1OpenedAt && (
                                <span className="active-trades-open-time-val">
                                  ({formatOpenTime(broker1OpenedAt)})
                                </span>
                              )}
                            </span>
                            <span className="active-trades-open-time-line">
                              <span className="active-trades-open-time-name">
                                {selectedAccountSet?.brokers?.[1]?.brokerName || "Broker 2"}
                              </span>
                              {broker2OpenedAt && (
                                <span className="active-trades-open-time-val">
                                  ({formatOpenTime(broker2OpenedAt)})
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td
                          className={`active-trades-cell active-trades-strong ${pnlToneClass(Number(trade.broker1Profit || 0))}`}
                        >
                          {money(trade.broker1Profit || 0)}
                        </td>
                        <td
                          className={`active-trades-cell active-trades-strong ${pnlToneClass(Number(trade.broker2Profit || 0))}`}
                        >
                          {money(trade.broker2Profit || 0)}
                        </td>
                        <td className="active-trades-cell active-trades-strong">
                          <PremiumPairValue
                            direction={trade.broker1Direction}
                            phase="entry"
                            primaryPremium={openingPremiumValue}
                            showEmptyRows={false}
                          />
                        </td>
                        <td className="active-trades-cell active-trades-strong">
                          {hasPremiumData ? (
                            <PremiumPairValue
                              direction={trade.broker1Direction}
                              phase="exit"
                              buyPremium={buyPremium}
                              sellPremium={sellPremium}
                            />
                          ) : (
                            <span className="active-trades-waiting">—</span>
                          )}
                        </td>
                        <td
                          className={`active-trades-cell active-trades-strong ${
                            hasPremiumData ? pnlToneClass(deficitValue) : ""
                          }`}
                        >
                          {hasPremiumData ? (
                            deficitValue.toFixed(2)
                          ) : (
                            <span className="active-trades-waiting">—</span>
                          )}
                        </td>
                        <td className="active-trades-cell">
                          <div className="active-trades-latency">
                            <span
                              className={`admin-badge ${getLatencyBadgeClass(trade.broker1Latency)}`}
                            >
                              B1{" "}
                              {Number.isFinite(Number(trade.broker1Latency))
                                ? `${trade.broker1Latency}ms`
                                : "—"}
                            </span>
                            <span
                              className={`admin-badge ${getLatencyBadgeClass(trade.broker2Latency)}`}
                            >
                              B2{" "}
                              {Number.isFinite(Number(trade.broker2Latency))
                                ? `${trade.broker2Latency}ms`
                                : "—"}
                            </span>
                          </div>
                        </td>
                        <td
                          className={`active-trades-cell ${pnlToneClass(Number(trade.swap || 0))}`}
                        >
                          {money(trade.swap || 0)}
                        </td>
                        <td
                          className={`active-trades-cell active-trades-total ${pnlToneClass(Number(trade.totalProfit || 0))}`}
                        >
                          {money(trade.totalProfit)}
                        </td>
                        <td className="active-trades-cell">
                          <label className="active-trades-scalping-toggle active-trades-scalping-toggle-inline">
                            <input
                              type="checkbox"
                              checked={Boolean(trade.scalpingMode)}
                              onChange={(e) => updateTradeScalpingMode(trade, e.target.checked)}
                              className="active-trades-scalping-input"
                              disabled={savingScalping.has(trade.tradeId)}
                            />
                            <div
                              className={`active-trades-scalping-switch${trade.scalpingMode ? " active" : ""}${savingScalping.has(trade.tradeId) ? " active-trades-scalping-switch-loading" : ""}`}
                            >
                              <div className="active-trades-scalping-knob" />
                            </div>
                          </label>
                        </td>
                        <td className="active-trades-cell">
                          {trade.pendingMode === "AI Smart Ladder" || trade.ladderGroupId ? (
                            <div className="active-trades-ladder-info">
                              <span title={trade.ladderGroupId || ""}>
                                Group{" "}
                                <strong className="active-trades-ladder-value">
                                  {maskLadderGroupId(trade.ladderGroupId)}
                                </strong>
                              </span>
                              <span>
                                Step{" "}
                                <strong className="active-trades-ladder-value">
                                  {trade.ladderStepIndex ?? "—"}
                                </strong>
                              </span>
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="active-trades-cell">
                          {(() => {
                            const tpRemaining = getTpRemainingParts(trade);
                            return (
                              <div
                                className={`active-trades-tp-value active-trades-tp-value-${String(trade.takeProfitMode || "none").toLowerCase()}`}
                              >
                                <span
                                  className={`active-trades-tp-mode-label active-trades-tp-mode-label-${String(trade.takeProfitMode || "none").toLowerCase()}`}
                                >
                                  {getTpModeLabel(trade)}
                                </span>
                                <span className="active-trades-tp-separator" aria-hidden="true" />
                                <span
                                  className={`active-trades-tp-value-text active-trades-tp-value-text-${String(trade.takeProfitMode || "none").toLowerCase()}`}
                                >
                                  {getTpDisplayValue(trade)}
                                  {trade.scalpingMode &&
                                    trade.takeProfit &&
                                    trade.takeProfitMode !== "None" && (
                                      <span className="active-trades-tp-value-meta">
                                        {" "}
                                        ({trade.scalpingConfirmationCount ?? 0}/3)
                                      </span>
                                    )}
                                </span>
                                {tpRemaining.visible && (
                                  <>
                                    <span
                                      className="active-trades-tp-separator"
                                      aria-hidden="true"
                                    />
                                    <span
                                      className={`active-trades-tp-remaining-label active-trades-tp-remaining-label-${String(trade.takeProfitMode || "none").toLowerCase()}`}
                                    >
                                      {tpRemaining.value && (
                                        <span
                                          className={
                                            tpRemaining.highlight
                                              ? "active-trades-tp-remaining-value-highlight"
                                              : ""
                                          }
                                        >
                                          {tpRemaining.value}
                                        </span>
                                      )}
                                      {tpRemaining.suffix}
                                    </span>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="active-trades-cell">
                          <div className="admin-table-actions">
                            <button
                              type="button"
                              onClick={() =>
                                startEditingTP(
                                  trade.tradeId,
                                  trade.takeProfit,
                                  trade.takeProfitMode,
                                )
                              }
                              className="admin-btn admin-btn-ghost active-trades-set-tp-btn"
                            >
                              <Edit2 size={15} />
                              <span>Set TP</span>
                            </button>
                          </div>
                        </td>
                        <td className="active-trades-cell">
                          <div className="admin-table-actions">
                            <button
                              onClick={() => closeTrade(trade)}
                              disabled={isCloseDisabled}
                              className={`admin-btn admin-btn-danger active-trades-close-btn${!canCloseTrades ? " active-trades-close-btn-waiting" : ""}${isCloseBusy ? " active-trades-close-btn-busy" : ""}`}
                              title={
                                isCloseBusy
                                  ? "Closing trade..."
                                  : closeTradeDisabledReason || "Close trade"
                              }
                            >
                              {isCloseBusy ? (
                                "Closing..."
                              ) : (
                                <>
                                  <XCircle size={16} />
                                  <span>Close</span>
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="admin-table-footer admin-users-table-footer active-trades-footer">
            <span className="admin-table-summary">
              Showing {rowStart}-{rowEnd} of {totalFilteredTrades}
            </span>
            <div className="admin-table-pagination">
              <button
                type="button"
                className="admin-btn admin-btn-ghost"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={15} />
                Prev
              </button>
              <span className="active-trades-pagecount">
                Page {currentPage} / {pageCount}
              </span>
              <button
                type="button"
                className="admin-btn admin-btn-ghost"
                onClick={() => setCurrentPage((prev) => Math.min(pageCount, prev + 1))}
                disabled={currentPage >= pageCount}
              >
                Next
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </section>

        <PageModal
          open={Boolean(activeTpTrade)}
          onClose={() => activeTpTrade && cancelEditingTP(activeTpTrade.tradeId)}
          onConfirm={() => {
            if (!activeTpTrade) return;
            saveTPValue(activeTpTrade.tradeId, activeTpValue, activeTpMode);
          }}
          title={
            activeTpTrade
              ? `Ticket #${activeTpTrade.broker1Ticket || activeTpTrade.tradeId}`
              : "Manage TP"
          }
          description="Set a premium or amount target, or clear the take-profit for this active trade."
          confirmLabel={
            savingTP.has(activeTpTrade?.tradeId)
              ? "Saving..."
              : activeTpMode === "None"
                ? "Clear TP"
                : "Save TP"
          }
          cancelLabel="Cancel"
          icon="info"
          loading={savingTP.has(activeTpTrade?.tradeId)}
        >
          {activeTpTrade && (
            <div className="active-trades-tp-modal-fields">
              <div className="active-trades-tp-modal-topline">
                <RadioButtonGroup
                  name={`tpMode-${activeTpTrade.tradeId}`}
                  value={activeTpMode}
                  onChange={(mode) => {
                    setTpModes((prev) => ({ ...prev, [activeTpTrade.tradeId]: mode }));
                    if (mode === "None") {
                      setTpValues((prev) => ({ ...prev, [activeTpTrade.tradeId]: "" }));
                      return;
                    }
                    if (mode === "Premium") {
                      setTpValues((prev) => ({
                        ...prev,
                        [activeTpTrade.tradeId]: formatTpDraftValue(
                          getDeficitPremiumValue(activeTpTrade),
                        ),
                      }));
                      return;
                    }
                    if (mode === "Amount") {
                      setTpValues((prev) => ({ ...prev, [activeTpTrade.tradeId]: "" }));
                    }
                  }}
                  options={[
                    { value: "None", label: "None", tone: "danger" },
                    { value: "Premium", label: "Premium", tone: "default" },
                    { value: "Amount", label: "Amount", tone: "success" },
                  ]}
                  className="active-trades-tp-radio-group"
                />
              </div>

              {activeTpMode !== "None" && (
                <input
                  type="number"
                  step={activeTpMode === "Premium" ? "0.00001" : "0.01"}
                  value={activeTpValue}
                  onChange={(e) => handleTpDraftChange(activeTpTrade.tradeId, e.target.value)}
                  onBlur={() => handleTpDraftBlur(activeTpTrade.tradeId)}
                  className="active-trades-tp-input active-trades-tp-modal-input"
                  placeholder={
                    activeTpMode === "Premium" ? "Current premium target" : "Profit target ($)"
                  }
                  autoFocus
                />
              )}

              {activeTpMode === "Premium" && (
                <div className="active-trades-tp-help">Deficit premium target</div>
              )}
              {activeTpMode === "Amount" && (
                <div className="active-trades-tp-help">Dollar amount target</div>
              )}
            </div>
          )}
        </PageModal>
        <IpAuditModal
          open={Boolean(selectedAuditIp)}
          ip={selectedAuditIp}
          onClose={() => setSelectedAuditIp("")}
        />
      </div>
    </div>
  );
}
